import {
  joinMembershipPath,
  membershipStatusPage,
  onboardingQuizPath,
  searchPath,
} from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { CurrentUser } from "@outschool/ui-auth";
import React from "react";
import { useLocation } from "react-router";

import { usePaymentMethods } from "../../hooks/CurrentUser";
import { useBannerDismissal } from "../../hooks/useBannerDismissal";
import { useCreditOffer } from "../../hooks/useCreditOffer";
import { useMembershipRequirement } from "../subscriptions/hooks/useMembershipRequirement";
import ArtShowcaseBanner, {
  IsValidTimeRangeForArtShowcaseBanner,
} from "./ArtShowcaseBanner";
import BackupPaymentBanner from "./BackupPaymentBanner";
import CreditOfferBanner from "./CreditOfferBanner";
import FirstMonthFreeBanner from "./FirstMonthFreeBanner";
import ReferralSignupBanner from "./ReferralSignupBanner";

const DEFAULT_EXCLUDED_PATHS = {
  exactPaths: [joinMembershipPath()],
  partialPaths: [
    onboardingQuizPath(),
    searchPath(),
    "/users/",
    membershipStatusPage(),
    "/teach/",
    "/account/teacher",
    "/class-comparison",
  ],
};

export function useShouldShowMembershipBannerByPath(): boolean {
  const location = useLocation();

  return React.useMemo(() => {
    if (DEFAULT_EXCLUDED_PATHS.exactPaths.includes(location.pathname)) {
      return false;
    } else if (
      DEFAULT_EXCLUDED_PATHS.partialPaths.some(path =>
        location.pathname.includes(path),
      )
    ) {
      return false;
    }

    return true;
  }, [location.pathname]);
}

/**
 * Render the appropriate membership banner based on the current user and location.
 * Only for logged-in users: see MembershipsBannerGate for logged-out users.
 */
export default function MembershipsBannerSwitcher({
  currentUser,
}: {
  currentUser: CurrentUser;
}) {
  // Top-level hooks.
  const { creditOffer, loading: creditOfferLoading } = useCreditOffer();
  const { shouldShowBannersToday, isBannerDismissed } = useBannerDismissal();
  const shouldShowBannerByPath = useShouldShowMembershipBannerByPath();
  const { mustPurchaseMembership } = useMembershipRequirement();
  const { loading: paymentMethodsLoading } = usePaymentMethods();
  const location = useLocation();

  const firstMonthFreeUser =
    mustPurchaseMembership && !currentUser?.hasEverSubscribed;

  // Special exception for the manual coupon experiment
  // Please remove this block when the experiment is complete
  // Override all other banner logic on the membership subscribe page for first month free users.
  if (
    firstMonthFreeUser &&
    location.pathname.includes("/membership/subscribe/")
  ) {
    return <FirstMonthFreeBanner />;
  }

  // Wait for credit offer and payment methods to load before rendering the banner.
  // Respect banner dismissal and exclusion paths.
  if (
    creditOfferLoading ||
    paymentMethodsLoading ||
    !shouldShowBannersToday ||
    !shouldShowBannerByPath
  ) {
    return null;
  }

  const musicShowcaseDismissed = isBannerDismissed("music_showcase");
  const shouldShowBackupBanner = !paymentMethodsLoading;
  const wasAccountCreatedAfterShowcaseBanner =
    currentUser && dayjs("2026-03-09").isBefore(currentUser?.created_at);

  if (
    !musicShowcaseDismissed &&
    IsValidTimeRangeForArtShowcaseBanner() &&
    !wasAccountCreatedAfterShowcaseBanner
  ) {
    return <ArtShowcaseBanner />;
  } else if (creditOffer) {
    return <CreditOfferBanner />;
  } else if (firstMonthFreeUser) {
    return <FirstMonthFreeBanner />;
  } else if (shouldShowBackupBanner) {
    return <BackupPaymentBanner />;
  } else {
    return <ReferralSignupBanner />;
  }
}
